import {
  captureAuthSessionOwner,
  isAuthSessionOwnerCurrent,
} from "@/shared/auth/session";
import {
  accountScopedStorageKey,
  currentAccountStorageScope,
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";

export type BriefCompletionActivitySource = "classic" | "reels" | "audio";
export type BriefCompletionActivityReceipt = {
  localDay: string;
  completedAtEpochMs: number;
  timeZoneId: string;
  source: BriefCompletionActivitySource;
  completionId: string;
};
export type BriefCompletionActivityOwner = ReturnType<typeof captureBriefCompletionActivityOwner>;
const KEY = "tb_public_web_brief_completion_activity_v1";
const EVENT = "tb-public-web-brief-completion-activity-changed";
const MAX_DAYS = 90;
const MAX_SERIALIZED_CHARS = 80_000;
let ownerGeneration = 0;
let observedWindow: Window | undefined;

function observeOwnerTransitions() {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function" || observedWindow === window) return;
  observedWindow = window;
  window.addEventListener("tb-auth-changed", () => { ownerGeneration += 1; });
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === "tb_auth_session_v1") ownerGeneration += 1;
  });
}

export function captureBriefCompletionActivityOwner() {
  observeOwnerTransitions();
  return { scope: currentAccountStorageScope(), auth: captureAuthSessionOwner(), generation: ownerGeneration };
}

function ownerIsCurrent(owner: BriefCompletionActivityOwner): boolean {
  return owner.generation === ownerGeneration && owner.scope === currentAccountStorageScope() && (owner.auth
    ? isAuthSessionOwnerCurrent(owner.auth) : captureAuthSessionOwner() === null);
}

function dayAt(epochMs: number, timeZoneId: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
      timeZone: timeZoneId, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date(epochMs));
    const part = (name: string) => parts.find((entry) => entry.type === name)?.value;
    return `${part("year")}-${part("month")}-${part("day")}`;
  } catch { return null; }
}

function currentTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function validReceipt(value: unknown, now: number): value is BriefCompletionActivityReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as BriefCompletionActivityReceipt;
  return ["classic", "reels", "audio"].includes(row.source) &&
    typeof row.completionId === "string" && Boolean(row.completionId.trim()) &&
    row.completionId.length <= 200 && !/[\u0000-\u001f\u007f]/.test(row.completionId) &&
    typeof row.timeZoneId === "string" && row.timeZoneId.length <= 100 &&
    typeof row.localDay === "string" && /^\d{4}-\d{2}-\d{2}$/.test(row.localDay) &&
    Number.isSafeInteger(row.completedAtEpochMs) && row.completedAtEpochMs > 0 &&
    row.completedAtEpochMs <= now && dayAt(row.completedAtEpochMs, row.timeZoneId) === row.localDay;
}

function bounded(receipts: BriefCompletionActivityReceipt[], now: number) {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - (MAX_DAYS - 1));
  const oldest = dayAt(cutoff.getTime(), currentTimeZone()) || "";
  const seen = new Set<string>();
  return receipts.filter((row) => validReceipt(row, now) && row.localDay >= oldest)
    .sort((first, second) => first.completedAtEpochMs - second.completedAtEpochMs)
    .filter((row) => { if (seen.has(row.localDay)) return false; seen.add(row.localDay); return true; })
    .slice(-MAX_DAYS).map((row) => ({ ...row }));
}

function read(now: number): { days: BriefCompletionActivityReceipt[]; writable: boolean; needsPrune?: boolean } {
  try {
    const raw = readAccountScopedStorage(KEY);
    if (!raw) return { days: [], writable: true };
    if (raw.length > MAX_SERIALIZED_CHARS) return { days: [], writable: false };
    const parsed = JSON.parse(raw);
    if (parsed?.schemaVersion !== 1 || !Array.isArray(parsed.days) || parsed.days.length > MAX_DAYS ||
      !parsed.days.every((row: unknown) => validReceipt(row, now))) return { days: [], writable: false };
    const days = bounded(parsed.days, now);
    return { days, writable: true, needsPrune: JSON.stringify(days) !== JSON.stringify(parsed.days) };
  } catch { return { days: [], writable: false }; }
}

function persist(owner: BriefCompletionActivityOwner, days: BriefCompletionActivityReceipt[]): boolean {
  if (!ownerIsCurrent(owner)) return false;
  try {
    writeAccountScopedStorage(KEY, JSON.stringify({ schemaVersion: 1, days }));
    return ownerIsCurrent(owner);
  } catch { return false; }
}

export function loadBriefCompletionActivity(nowEpochMs = Date.now()): BriefCompletionActivityReceipt[] {
  if (!Number.isSafeInteger(nowEpochMs) || nowEpochMs <= 0) return [];
  const owner = captureBriefCompletionActivityOwner();
  const state = read(nowEpochMs);
  if (state.writable && state.needsPrune) persist(owner, state.days);
  return state.days;
}

/** Call only after the exact delivered recording or complete reading archive is durable. */
export function recordBriefCompletionActivity(input: {
  owner: BriefCompletionActivityOwner;
  source: BriefCompletionActivitySource;
  completionId: string;
  completedAtEpochMs?: number;
}): boolean {
  const now = Date.now();
  if (!ownerIsCurrent(input.owner)) return false;
  const completedAtEpochMs = input.completedAtEpochMs ?? now;
  const timeZoneId = currentTimeZone();
  const row: BriefCompletionActivityReceipt = { source: input.source, completionId: input.completionId,
    completedAtEpochMs, timeZoneId, localDay: dayAt(completedAtEpochMs, timeZoneId) || "" };
  if (!validReceipt(row, now)) return false;
  const current = read(now);
  if (!current.writable) return false;
  if (current.days.some((receipt) => receipt.localDay === row.localDay)) {
    return current.needsPrune ? persist(input.owner, current.days) : true;
  }
  const days = bounded([...current.days, row], now);
  if (!days.some((receipt) => receipt.completionId === row.completionId) || !persist(input.owner, days)) return false;
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENT));
  return true;
}

export function onBriefCompletionActivityChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === accountScopedStorageKey(KEY)) listener();
  };
  window.addEventListener(EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => { window.removeEventListener(EVENT, listener); window.removeEventListener("storage", onStorage); };
}
